// Starter looks for the avatar create flow (/welcome and "Create avatar").
// Images live in public/avatar-styles/ as `<id>.webp` (card, 512w) and
// `<id>@2x.webp` (1024w). Each is a full-body A-pose on a plain background, so
// the picture itself can be the reference the 3D build works from.
//
// Every look is its own card: the seven realistic people (from her
// ~/Downloads/people set) used to hide behind one "Real Person" card.

export type StarterKind = 'realistic' | 'stylized';

export interface AvatarStyle {
  id: string;
  /** Card label. */
  name: string;
  tagline: string;
  kind: StarterKind;
  /** The style as named to the selfie restyle ("draw me in this style"). */
  styleName: string;
}

const real = (id: string, name: string): AvatarStyle => ({ id, name, tagline: 'Realistic', kind: 'realistic', styleName: 'Realistic person' });
const styl = (id: string, name: string, tagline: string): AvatarStyle => ({ id, name, tagline, kind: 'stylized', styleName: name });

export const AVATAR_STYLES: AvatarStyle[] = [
  real('real-black-woman', 'Woman · Black'),
  real('real-white-woman', 'Woman · White'),
  real('real-asian-woman', 'Woman · East Asian'),
  real('real-latina-woman', 'Woman · Latina'),
  real('real-white-man', 'Man · White'),
  real('real-asian-man', 'Man · East Asian'),
  real('real-latino-man', 'Man · Latino'),
  styl('streetwear', 'Streetwear', '90s varsity, fresh kicks'),
  styl('glam-doll', 'Glam Doll', 'Big eyes, Y2K glam'),
  styl('afrofuturist', 'Afrofuturist', 'Royal, golden, future'),
  styl('fantasy', 'Fantasy', 'Elves, robes and magic'),
  styl('anime', 'Anime', 'Cel-shaded hero'),
  styl('toon-3d', 'Toon 3D', 'Animated-movie warmth'),
  styl('cyberpunk', 'Cyberpunk', 'Neon street runner'),
  styl('chibi', 'Chibi', 'Tiny body, big heart'),
  styl('low-poly', 'Low-Poly', 'Faceted indie-game look'),
];

export const styleCard = (id: string) => `/avatar-styles/${id}.webp`;
export const styleDetail = (id: string) => `/avatar-styles/${id}@2x.webp`;

/** The style picture as a JPEG data URL — what the 3D build and restyle accept. */
export async function styleImageDataUrl(id: string): Promise<string> {
  const img = new Image();
  img.src = styleDetail(id);
  await img.decode();
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext('2d')!.drawImage(img, 0, 0);
  return c.toDataURL('image/jpeg', 0.92);
}

/** A small JPEG of the card, remembered so a reload mid-build can show what's building. */
export async function styleThumbDataUrl(src: string, width = 200): Promise<string | undefined> {
  try {
    const img = new Image();
    img.src = src;
    await img.decode();
    const c = document.createElement('canvas');
    c.width = width;
    c.height = Math.round((img.naturalHeight / img.naturalWidth) * width);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.7);
  } catch { return undefined; }
}
